import { buildSympyCode, parseNodeToSympy, semanticEquals } from "@corca/sympy";
import { LRUCache } from "lru-cache";

import { pyodideManager } from "@/components/pyodide/PyodideManager";
import { createLogger } from "@/utils/logger";
import { trySympy } from "@/utils/sympyErrors";

import { groupSuggestions } from "../utils";

import { executeCode } from "./utils/getPhantomHints";
import { nodeToExpressionText } from "./utils/nodeToExpressionText";
import { normalizeSuggestionKeywords } from "./utils/normalizeKeywords";

import type {
  SingleSuggestion,
  SuggestionProvider,
  SympyActionType,
} from "@/components/suggestions/types";
import type { NodeJson } from "@corca/data";

const logger = createLogger("SympyActionSuggestions");

export const SYMPY_ACTIONS: SympyActionType[] = ["factor", "expand", "simplify"];

const NESTED_KEY = "sympyAction";

const HINT_CACHE_MAX_SIZE = 500;

const hintResults = new LRUCache<string, { result: NodeJson | undefined }>({
  max: HINT_CACHE_MAX_SIZE,
});

const createSuggestion = (
  action: SympyActionType,
  preview?: NodeJson,
): SingleSuggestion<"sympyAction"> => {
  let nodesJson: NodeJson[] | undefined;
  if (preview) {
    nodesJson = preview.type === "inline" ? preview.children : [preview];
  }

  return {
    type: "single",
    item: {
      type: "sympyAction",
      intlName: `operator.${action}`,
      symbol: action.charAt(0).toUpperCase() + action.slice(1),
      keywords: normalizeSuggestionKeywords({ keywords: [action] }),
      nodesJson,
    },
    action: {
      type: "sympy-action",
      command: action,
    },
    trackingId: `sympy.${action}`,
    popupGroup: "hints",
    priority: 20,
    substitution: {
      cursorPlacement: "after",
    },
  };
};

const createParentSuggestion = (
  children: SingleSuggestion<"sympyAction">[],
): SingleSuggestion<"sympyAction">[] => {
  const [first] = children;

  if (!first) {
    return [];
  }

  return [
    {
      ...first,
      item: {
        type: "sympyAction",
        intlName: "operator.simplify",
        symbol: "",
        keywords: normalizeSuggestionKeywords({ keywords: SYMPY_ACTIONS }),
      },
      icon: "Simplify",
      forceIcon: true,
      trackingId: undefined,
      nestedKey: NESTED_KEY,
      nestedItems: groupSuggestions("", children),
    },
  ];
};

export const sympyActionSuggestionsProvider: SuggestionProvider = {
  type: "factor",
  search: (_str, doc) => {
    const { selection } = doc;
    if (!selection.isMathOnlySelection || !pyodideManager.isReady) {
      return [];
    }

    const nodes = selection.selectedNodes;
    const inlineNodeJson: NodeJson = {
      type: "inline",
      props: {},
      children: nodes.map((n) => n.toJSON()),
    };
    const inputExpressionText = nodeToExpressionText(inlineNodeJson, { normalizeExp: true });
    const cacheKey = (action: SympyActionType) => `${inputExpressionText}:${action}`;

    const getPreview = async (action: SympyActionType) => {
      const parsed = trySympy(() => parseNodeToSympy(inlineNodeJson));
      if ("error" in parsed) return undefined;

      if (!parsed.expr) return undefined;
      parsed.warnings.forEach((warning) => logger.warn(warning));

      const buildResult = trySympy(() => buildSympyCode(action, parsed.expr, parsed.variables));
      if ("error" in buildResult) return undefined;

      const res = await executeCode(buildResult.expr, parsed.symbolNodes, parsed.hasDecimal);
      if (!res?.json) {
        return undefined;
      }

      // Hide the preview when the result is equivalent to the input (nothing to show).
      if (semanticEquals(parsed.semantic, res.outputSemantic)) {
        return undefined;
      }

      return res.json;
    };

    if (SYMPY_ACTIONS.every((action) => hintResults.has(cacheKey(action)))) {
      const children = SYMPY_ACTIONS.map((action) =>
        createSuggestion(action, hintResults.get(cacheKey(action))?.result),
      ).filter((suggestion) => suggestion.item.nodesJson);

      return [createParentSuggestion(children)];
    }

    const suggestions = Promise.all(
      SYMPY_ACTIONS.map(async (action) => {
        const key = cacheKey(action);
        const cached = hintResults.get(key);

        let preview: NodeJson | undefined;
        if (cached) {
          preview = cached.result;
        } else {
          preview = await getPreview(action);
          hintResults.set(key, { result: preview });
        }

        const suggestion = createSuggestion(action, preview);
        // Only keep the action if a preview is available
        return suggestion.item.nodesJson ? suggestion : undefined;
      }),
    ).then((results) => createParentSuggestion(results.filter((s) => !!s)));

    return [suggestions];
  },
};
